import type { UiSearchResult } from '@search/core/SearchResultMapper';

export interface SearchClientResult {
  results: UiSearchResult[];
  error?: {
    status: number;
    message: string;
  };
}

export class SearchClient {
  private controller: AbortController | null = null;

  async search(params: {
    query: string;
    limit?: number;
    vaultId?: string;
    org?: string;
    useSemantic?: boolean;
    scope?: 'public' | 'draft';
    grouped?: boolean;
    maxMatchesPerPage?: number;
  }): Promise<SearchClientResult> {
    // Cancel any in-flight request for responsiveness while typing
    if (this.controller) {
      try { this.controller.abort(); } catch {}
    }
    this.controller = new AbortController();

    const q = new URLSearchParams();
    q.set('q', params.query);
    if (params.limit) q.set('limit', String(params.limit));
    if (params.vaultId) q.set('vaultId', params.vaultId);
    if (params.org) q.set('org', params.org);
    if (params.scope) q.set('scope', params.scope);
    if (params.useSemantic) q.set('useSemantic', 'true');
    // Explicitly send grouped param (default true) so server can distinguish false from undefined
    q.set('grouped', String(params.grouped ?? true));
    if (params.maxMatchesPerPage) q.set('maxMatchesPerPage', String(params.maxMatchesPerPage));
    let res: Response | null = null;
    try {
      res = await fetch(`/api/search?${q.toString()}`, { cache: 'no-store', signal: this.controller.signal });
    } catch (err: any) {
      // Swallow aborts; propagate other errors
      if (err?.name === 'AbortError') return { results: [] };
      throw err;
    } finally {
      // Reset controller once the request settles
      this.controller = null;
    }
    if (!res) return { results: [] };
    if (!res.ok) {
      // Return error info so UI can display appropriate message
      const errorData = await res.json().catch(() => ({}));
      return {
        results: [],
        error: {
          status: res.status,
          message: errorData?.error ?? (res.status === 403 ? 'Insufficient privileges' : 'Search failed'),
        },
      };
    }
    const data = await res.json();
    return { results: (data?.results ?? []) as UiSearchResult[] };
  }
}

export const searchClient = new SearchClient();
